import type { FamilyMatrixVariant } from 'apis/supplier-prices';
import type { PriceTier, UomLadder } from 'utils/price-units';
import { derivePrices, formatMoney, resolveTier, tierUnit } from 'utils/price-units';

// UOM ladder display for the price matrix: pack (packUnit × packSize) contains
// items (itemUnit), each holding netContent of baseUnit.

export type VariantUom = Pick<FamilyMatrixVariant, 'baseUnit' | 'netContent' | 'itemUnit' | 'packUnit' | 'packSize'>;

// BE numerics can arrive as strings; trailing zeros drop via Number().
function quantity(value: number | null | undefined): string | null {
  const parsed = Number(value);
  return value == null || Number.isNaN(parsed) ? null : String(parsed);
}

// Item content in base units, e.g. "1.5 kg"; bare base unit when the content
// is unknown.
function sizeValue(variant: VariantUom): string | null {
  const content = quantity(variant.netContent);
  if (!variant.baseUnit) return null;
  return content ? `${content} ${variant.baseUnit}` : variant.baseUnit;
}

// Chip values for the matrix filter section, one per UOM dimension. Null means
// the variant doesn't carry that rung (no chip contribution).
export function uomFilterValues(variant: VariantUom): {
  pack: string | null;
  item: string | null;
  size: string | null;
} {
  const packSize = quantity(variant.packSize);
  return {
    pack: variant.packUnit ? (packSize ? `${variant.packUnit} × ${packSize}` : variant.packUnit) : null,
    item: variant.itemUnit || null,
    size: sizeValue(variant),
  };
}

// The variant as a price-units ladder. price-units treats non-numbers as
// UNKNOWN rungs, and the matrix BE can serialize netContent/packSize as
// strings — coerce them so "12" still converts.
export function variantLadder(variant: VariantUom): UomLadder {
  const num = (value: number | null | undefined): number | null => {
    const parsed = Number(value);
    return value == null || Number.isNaN(parsed) ? null : parsed;
  };
  return {
    baseUnit: variant.baseUnit,
    itemUnit: variant.itemUnit,
    netContent: num(variant.netContent),
    packUnit: variant.packUnit,
    packSize: num(variant.packSize),
  };
}

// The muted restatement lines under a matrix price, one per rung ("33 / pcs",
// "16.50 / kg") — stacked instead of dot-joined, so they never wrap into
// unreadable fragments inside a price-width cell. The price is restated FROM
// the rung it was agreed on: the deal's stored dealUnit when given (and still
// defined on the variant), else the outermost rung (the pre-dealUnit matrix
// convention). Empty when nothing is derivable, so callers drop the block.
export function derivedPriceLines(
  variant: VariantUom,
  price: number | null | undefined,
  dealUnit?: PriceTier | null
): string[] {
  const amount = Number(price);
  if (price == null || !Number.isFinite(amount) || amount <= 0) return [];

  const ladder = variantLadder(variant);
  const tier = resolveTier(ladder, dealUnit);
  if (!tier) return [];
  return derivePrices(amount, tier, ladder).map((entry) => `${formatMoney(entry.amount)} / ${entry.unit}`);
}

// The unit word a deal price is quoted in — the stored rung when resolvable,
// else the outermost rung. Null when the variant defines no rung at all.
export function dealUnitWord(variant: VariantUom, dealUnit?: PriceTier | null): string | null {
  const ladder = variantLadder(variant);
  const tier = resolveTier(ladder, dealUnit);
  return tier ? tierUnit(ladder, tier) : null;
}

// Row line: "ctn × 12 btl × 1.5 kg". The base rung shows only with a known
// content (a trailing "× kg" reads as a conversion nobody has) — unless it is
// the only rung the variant carries.
export function uomLadder(variant: VariantUom): string {
  const packSize = quantity(variant.packSize);
  const content = quantity(variant.netContent);

  const item = packSize ? `${packSize} ${variant.itemUnit ?? ''}`.trim() : variant.itemUnit;
  const base = content ? `${content} ${variant.baseUnit}` : null;

  const rungs = [variant.packUnit, item, base].filter(Boolean);
  return rungs.length ? rungs.join(' × ') : variant.baseUnit || '';
}
